import { qoursesApi } from '@/api/qourses.tsx'
import { CourseName } from '@/components/Resolvers.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import useGetCourse, { getCourseQueryKey } from '@/hooks/courses/useGetCourse.tsx'
import { getCoursesQueryKey } from '@/hooks/courses/useGetCourses.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { ToastVariant, minDelay, sendGenericErrorNotification, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { ArchiveRestore, Calendar, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function RestoreCourseModal({ courseId }: { courseId: string }) {
  const { course, isLoading, isError } = useGetCourse(courseId)
  const [isRestoring, setIsRestoring] = useState(false)

  const queryClient = useQueryClient()

  const { t: translate } = useTranslation()

  const handleRestoreCourse = async () => {
    try {
      setIsRestoring(true)
      await minDelay(qoursesApi.course.courseControllerRestoreCourseV1(course.id), 1000)
      sendNotification(
        translate('modals.restoreCourseModal.notification.title'),
        translate('modals.restoreCourseModal.notification.subtitle'),
        ToastVariant.Success,
      )
      popAllModals()
      queryClient.invalidateQueries(getCoursesQueryKey({}))
      queryClient.invalidateQueries(getCourseQueryKey(courseId))
    } catch (e) {
      console.error(e)
      sendGenericErrorNotification()
    } finally {
      setIsRestoring(false)
    }
  }

  if (isLoading) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('common.loading')}</DialogTitle>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  if (isError) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('modals.restoreCourseModal.errorState.title')}</DialogTitle>
          <DialogDescription>
            {translate('modals.restoreCourseModal.errorState.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogHeader className="mb-2">
          <DialogTitle className="mt-6 sm:mt-2"></DialogTitle>
          <DialogTitle className="mt-6 flex flex-col gap-y-2 sm:mt-2">
            <div className="flex flex-row items-center justify-center text-sm text-muted-foreground sm:justify-start">
              <Calendar className="mr-1 size-4 text-muted-foreground" />
              <CourseName courseId={courseId} />
            </div>
            <p>{translate('modals.restoreCourseModal.title')}</p>
          </DialogTitle>
          <DialogDescription>{translate('modals.restoreCourseModal.subtitle')}</DialogDescription>
        </DialogHeader>
      </DialogHeader>
      <Button
        variant="indigo"
        className="bezel"
        disabled={isRestoring}
        onClick={handleRestoreCourse}
      >
        {isRestoring ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {translate('common.loading')}
          </>
        ) : (
          <>
            <ArchiveRestore className="mr-2 h-4 w-4" />
            {translate('modals.restoreCourseModal.button')}
          </>
        )}
      </Button>
    </Dynamic.Content>
  )
}
